import { StackScreenProps } from "@react-navigation/stack";
import { useTranslation } from "react-i18next";
import {
  Button,
  Page,
  Stack,
  Card,
  Text,
  FlatActionList,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { DELETE_AI_STRATEGY_MUTATION } from "../../common";
import { useMutation } from "@apollo/client";
import { Deleted } from "@towersystems/roam-common/lib/generated-types";
import React from "react";
import { ViewLlmStrategyRootScreenParamList } from "../../../../types";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { FlatListSearchableSkeleton } from "../../../../components/FlatListSearchableSkeleton";
import { ScreenContext } from "./common";

export interface ViewLlmStrategyScreenProps
  extends StackScreenProps<
    ViewLlmStrategyRootScreenParamList,
    "ViewLlmStrategyScreen"
  > {}

export const ViewLlmStrategyScreen = ({
  navigation,
  route,
}: ViewLlmStrategyScreenProps) => {
  const { data } = React.useContext(ScreenContext);
  const llmStrategy = data?.strategy;

  const { t } = useTranslation("settings");
  const transitionEnded = useHasTransitionEnded(navigation);
  const flashMessagesContext = useFlashMessages();

  const [m] = useMutation<{
    deleteAiStrategy: Deleted;
  }>(DELETE_AI_STRATEGY_MUTATION);

  if (llmStrategy === undefined || !transitionEnded) {
    return <FlatListSearchableSkeleton searchbar={false} />;
  }

  const handleOnSubmit = async (id: string) => {
    try {
      await m({
        variables: {
          id,
        },
      });
      flashMessagesContext.showMessage({
        message: "AI strategy deleted successfully",
        severity: "success",
      });
      navigation.goBack();
    } catch (error) {
      console.error("Error deleting LLM strategy:", error);
    }
  };

  const footerMarkup = (
    <Button
      disabled={llmStrategy === undefined}
      label={t("button.delete_llm_strategy")}
      isDestructive
      onPress={() => handleOnSubmit(llmStrategy?.id ?? "xx")}
    ></Button>
  );

  let strategyConfigMarkup;

  switch (llmStrategy?.config.code) {
    case "openai":
      strategyConfigMarkup = (
        <Stack vertical>
          <FlatActionList
            items={[
              {
                key: "llm_strategy_openai_model",
                title: t("form.llm_strategy_openai_model.label"),
                subTitle: t("form.llm_strategy_openai_model.description"),
                onPress: () => {
                  navigation.navigate("UpdateLlmModelScreen");
                },
                componentRight: () => (
                  <Text {...textActionButtonProps}>
                    {llmStrategy?.config.args.find(
                      (arg) => arg.name === "model"
                    )?.value || ""}
                  </Text>
                ),
              },
              {
                key: "llm_strategy_openai_api_key",
                title: t("form.llm_strategy_openai_api_key.label"),
                subTitle: t("form.llm_strategy_openai_api_key.description"),
                onPress: () => {
                  navigation.navigate("GenericUpdateLlmConfigScreen", {
                    arg: "apiKey",
                    placeholder: "Enter new OpenAI API Key",
                    title: "OpenAI API Key",
                    description:
                      "Replace your OpenAI API Key. It will not be displayed again for security reasons.",
                  });
                },
                componentRight: () => (
                  <Text {...textActionButtonProps}>Replace API Key</Text>
                ),
              },
            ]}
          />
        </Stack>
      );
      break;
    case "anthropic":
      strategyConfigMarkup = (
        <Stack vertical>
          <FlatActionList
            items={[
              {
                key: "llm_strategy_anthropic_model",
                title: t("form.llm_strategy_anthropic_model.label"),
                subTitle: t("form.llm_strategy_anthropic_model.description"),
                onPress: () => {
                  navigation.navigate("UpdateLlmModelScreen");
                },
                componentRight: () => (
                  <Text {...textActionButtonProps}>
                    {llmStrategy?.config.args.find(
                      (arg) => arg.name === "model"
                    )?.value || ""}
                  </Text>
                ),
              },
              {
                key: "llm_strategy_anthropic_api_key",
                title: t("form.llm_strategy_anthropic_api_key.label"),
                subTitle: t("form.llm_strategy_anthropic_api_key.description"),
                onPress: () => {
                  navigation.navigate("GenericUpdateLlmConfigScreen", {
                    arg: "apiKey",
                    placeholder: "Enter new Anthropic API Key",
                    title: "Anthropic API Key",
                    description:
                      "Replace your Anthropic API Key. It will not be displayed again for security reasons.",
                  });
                },
                componentRight: () => (
                  <Text {...textActionButtonProps}>Replace API Key</Text>
                ),
              },
            ]}
          />
        </Stack>
      );
      break;

    case "gemini":
      strategyConfigMarkup = (
        <Stack vertical>
          <FlatActionList
            items={[
              {
                key: "llm_strategy_gemini_model",
                title: t("form.llm_strategy_gemini_model.label"),
                subTitle: t("form.llm_strategy_gemini_model.description"),
                onPress: () => {
                  navigation.navigate("UpdateLlmModelScreen");
                },
                componentRight: () => (
                  <Text {...textActionButtonProps}>
                    {llmStrategy?.config.args.find(
                      (arg) => arg.name === "model"
                    )?.value || ""}
                  </Text>
                ),
              },
              {
                key: "llm_strategy_gemini_api_key",
                title: t("form.llm_strategy_gemini_api_key.label"),
                subTitle: t("form.llm_strategy_gemini_api_key.description"),
                onPress: () => {
                  navigation.navigate("GenericUpdateLlmConfigScreen", {
                    arg: "apiKey",
                    placeholder: "Enter new Gemini API Key",
                    title: "Gemini API Key",
                    description:
                      "Replace your Gemini API Key. It will not be displayed again for security reasons.",
                  });
                },
                componentRight: () => (
                  <Text {...textActionButtonProps}>Replace API Key</Text>
                ),
              },
            ]}
          />
        </Stack>
      );
      break;
    case "openai_dalle":
      strategyConfigMarkup = (
        <Stack vertical>
          <FlatActionList
            items={[
              {
                key: "llm_strategy_openai_dalle_model",
                title: t("form.llm_strategy_openai_dalle_model.label"),
                subTitle: t("form.llm_strategy_openai_dalle_model.description"),
                onPress: () => {
                  navigation.navigate("UpdateLlmModelScreen");
                },
                componentRight: () => (
                  <Text {...textActionButtonProps}>
                    {llmStrategy?.config.args.find(
                      (arg) => arg.name === "model"
                    )?.value || ""}
                  </Text>
                ),
              },
              {
                key: "llm_strategy_openai_dalle_api_key",
                title: t("form.llm_strategy_openai_dalle_api_key.label"),
                subTitle: t(
                  "form.llm_strategy_openai_dalle_api_key.description"
                ),
                onPress: () => {
                  navigation.navigate("GenericUpdateLlmConfigScreen", {
                    arg: "apiKey",
                    placeholder: "Enter new OpenAI API Key",
                    title: "OpenAI API Key",
                    description:
                      "Replace your OpenAI API Key. It will not be displayed again for security reasons.",
                  });
                },
                componentRight: () => (
                  <Text {...textActionButtonProps}>Replace API Key</Text>
                ),
              },
            ]}
          />
        </Stack>
      );
      break;
    default:
      strategyConfigMarkup = null;
  }

  return (
    <Page
      title="View AI Strategy"
      secondaryTitle="AI strategy details"
      footer={footerMarkup}
    >
      <Stack vertical>
        <Card title="Details">
          <Stack vertical>
            <FlatActionList
              items={[
                {
                  key: "llm_strategy_name",
                  title: t("form.llm_strategy_name.label"),
                  subTitle: t("form.llm_strategy_name.description"),
                  componentRight: () => (
                    <Text {...textButtonProps}>{llmStrategy?.name}</Text>
                  ),
                },
                {
                  key: "llm_strategy_code",
                  title: t("form.llm_strategy_code.label"),
                  subTitle: t("form.llm_strategy_code.description"),
                  componentRight: () => (
                    <Text {...textButtonProps}>
                      {llmStrategy?.config.code || "-"}
                    </Text>
                  ),
                },
                {
                  key: "ai_type",
                  title: "AI Model Type",
                  subTitle: "Type of AI Model",
                  componentRight: () => (
                    <Text {...textButtonProps}>
                      {llmStrategy?.aiType || "-"}
                    </Text>
                  ),
                },
              ]}
            />
          </Stack>
        </Card>
        {strategyConfigMarkup && (
          <Card title="Configuration">{strategyConfigMarkup}</Card>
        )}
      </Stack>
    </Page>
  );
};

const textButtonProps = {
  fontWeight: "bold",
  variant: "body",
} as any;

const textActionButtonProps = {
  ...textButtonProps,
  color: "action",
};
